import { Command, Plugin, Widget, toWidget, viewToModelPositionOutsideModelElement, type ViewElement } from "ckeditor5";

import type { DowncastWriter, Element as ModelElement } from "ckeditor5";

type VariableMeta = {
  key: string;
  datasource: string;
  depth: number;
  description: string;
};

export const VARIABLE_BY_KEY = new Map<string, VariableMeta>();

export const populateVariableRegistry = (variableMap: Record<string, VariableMeta>) => {
  VARIABLE_BY_KEY.clear();
  for (const [flatKey, details] of Object.entries(variableMap)) {
    VARIABLE_BY_KEY.set(flatKey, details);
  }
};

export const VARIABLE_ELEMENT = "templateVariable";

class InsertTemplateVariable extends Command {
  public override refresh(): void {
    const model = this.editor.model;
    const focus = model.document.selection.focus;
    this.isEnabled = focus !== null && model.schema.checkChild(focus, VARIABLE_ELEMENT);
  }

  public override execute(key: string): void {
    const model = this.editor.model;
    model.change((writer) => {
      const element = writer.createElement(VARIABLE_ELEMENT, { key });
      model.insertObject(element, null, null, { setSelection: "after" });
    });
  }
}

const buildView = (modelElement: ModelElement, writer: DowncastWriter, forEditing: boolean): ViewElement => {
  const key = String(modelElement.getAttribute("key") ?? "");
  const meta = VARIABLE_BY_KEY.get(key);
  const source = meta?.datasource ?? "APPLICATION";
  const view = writer.createContainerElement("span", {
    class: meta === undefined ? "tpl-variable tpl-variable--unknown" : "tpl-variable",
    "data-variable": key,
    "data-source": source,
    title: meta === undefined ? `${key} is not in the variable list` : `${key} — ${meta.description}`,
  });
  const text = forEditing ? key : `{{${key}}}`;
  writer.insert(writer.createPositionAt(view, 0), writer.createText(text));
  return view;
};

export class TemplateVariableEditing extends Plugin {
  public static get requires() {
    return [Widget] as const;
  }

  public static get pluginName() {
    return "TemplateVariableEditing" as const;
  }

  public init(): void {
    const editor = this.editor;

    editor.model.schema.register(VARIABLE_ELEMENT, {
      isInline: true,
      isObject: true,
      allowWhere: "$text",
      allowAttributesOf: "$text",
      allowAttributes: ["key"],
    });

    editor.conversion.for("upcast").elementToElement({
      view: { name: "span", attributes: { "data-variable": true } },
      model: (viewElement, { writer }) =>
        writer.createElement(VARIABLE_ELEMENT, { key: viewElement.getAttribute("data-variable") }),
    });

    editor.conversion.for("editingDowncast").elementToElement({
      model: VARIABLE_ELEMENT,
      view: (modelElement, { writer }) => {
        const key = String(modelElement.getAttribute("key") ?? "");
        return toWidget(buildView(modelElement, writer, true), writer, {
          label: key,
        });
      },
    });

    editor.conversion.for("dataDowncast").elementToElement({
      model: VARIABLE_ELEMENT,
      view: (modelElement, { writer }) => buildView(modelElement, writer, false),
    });

    editor.editing.mapper.on(
      "viewToModelPosition",
      viewToModelPositionOutsideModelElement(editor.model, (viewElement) => viewElement.hasClass("tpl-variable")),
    );

    editor.commands.add("insertTemplateVariable", new InsertTemplateVariable(editor));
  }
}
